/**
 * В каком порядке брать ноги: первой — та, где выше шанс не встать.
 *
 * Правило несимметрично, и в этом весь смысл. Отказ на ПЕРВОЙ ноге стоит ноль:
 * ставки ещё нет, человек закрыл вкладку и пошёл дальше. Отказ на второй
 * оставляет голую позицию на всю сумму первой — то есть обычную одиночную
 * ставку по кэфу, который никто не выбирал. Поэтому сомнительное делается
 * раньше, пока отказ бесплатен, а не позже, когда он стоит банка.
 *
 * Считается риск из того, что ленте известно В МОМЕНТ НАЖАТИЯ, и только из
 * причин, у каждой из которых есть механизм отказа:
 *
 * | Признак | Почему нога может не встать |
 * |---|---|
 * | биржевая нога | лимитка стоит в стакане и наливается не вся или не сразу |
 * | нога «против» | принимаем чужую ставку: встречный объём тоньше |
 * | адреса нет вовсе | контору и матч человек ищет руками — это десятки секунд |
 * | адрес до раздела | матч на странице ещё надо найти |
 * | лимит не назван | сколько примет контора, не знает никто, включая нас |
 *
 * Веса — про механизм отказа, не про замер донора V2. На V1 отказ чаще стоит
 * на секционной ссылке и пустом URL, чем на биржевой ноге (биржа не в каждом
 * кадре). После прибора `failed_venue` числа можно пересчитать; пока держим
 * порядок «нет адреса > раздел > биржа».
 *
 * Чего здесь НЕТ намеренно. Готовность площадки (разлогинило, гео-блок, мало
 * денег) — самый сильный признак из всех, но живёт он в расширении рядом с
 * человеком, а лента про его счета не знает ничего и знать не должна. Порядок
 * в ленте строится на общедоступном, панель расширения добавляет к нему своё.
 *
 * И порядок остаётся СОВЕТОМ: кнопка второй ноги нажимается в любой момент.
 * Вилочник знает про очередь в кассе то, чего не знает лента (08.08, владелец).
 *
 * Исключения — по площадке, не по классу (`leadOf`). С 28.09 Polymarket первым
 * только при тонком стакане: при глубоком маркет-ордер нальётся на ту сумму,
 * которую приняла контора, а обратный порядок оставлял шары без пары, когда
 * контора отказывала (вилки практика 72, 76, 81, 98, 107).
 *
 * Это совет по умолчанию. В студии можно поставить `legsFirst: 'poly'` —
 * полимаркет всегда первая нога, даже с глубоким стаканом (Антон, 28.09).
 */

import { HAND_SOURCE, type RowLeg } from '../lib/row';
import { polyMarketAllowed } from '../poly/gate';
import { useSettings } from '../store/settings';

/** Вес признака в шансе не встать. Числа грубые: важен порядок, не точность. */
const WEIGHT = {
  exchange: 40,
  lay: 10,
  noUrl: 35,
  section: 22,
  event: 8,
  capUnknown: 8,
} as const;

export interface Risk {
  /** Больше — выше шанс не встать. Сравнивают только между ногами одной вилки. */
  score: number;
  /** Главная причина словами. `null` — ничего примечательного. */
  why: string | null;
}

/** Насколько нога может не встать и почему. */
export function riskOf(leg: RowLeg): Risk {
  if (leg.source === HAND_SOURCE) return { score: 0, why: null };
  let score = 0;
  const reasons: { weight: number; say: string }[] = [];

  if (leg.unit === 'share') {
    score += WEIGHT.exchange;
    // По рынку на спорте сайт держит ордер секунды: за них стакан уезжает.
    const byMarket = isPolymarket(leg.source) && leg.marketDead !== undefined
      && polyMarketAllowed() && useSettings.getState().polyOrder === 'market';
    reasons.push({
      weight: WEIGHT.exchange,
      say: byMarket ? 'по рынку: стакан может уехать за задержку сайта' : 'лимитка может не налиться',
    });
  }
  if (leg.lay) {
    score += WEIGHT.lay;
    reasons.push({ weight: WEIGHT.lay, say: 'ставка против: встречный объём тоньше' });
  }
  if (leg.url === null) {
    score += WEIGHT.noUrl;
    reasons.push({ weight: WEIGHT.noUrl, say: 'адреса нет, контору искать руками' });
  } else if (leg.precision === 'section' || leg.precision === 'venue') {
    score += WEIGHT.section;
    reasons.push({ weight: WEIGHT.section, say: 'ссылка ведёт в раздел, матч искать самому' });
  } else if (leg.precision === 'event') {
    score += WEIGHT.event;
    reasons.push({ weight: WEIGHT.event, say: 'ссылка на матч, исход выбирать' });
  }
  if (leg.cap.kind === 'assumed' && leg.cap.value === null) {
    score += WEIGHT.capUnknown;
    reasons.push({ weight: WEIGHT.capUnknown, say: 'лимит конторы не назван' });
  }

  reasons.sort((a, b) => b.weight - a.weight);
  return { score, why: reasons[0]?.say ?? null };
}

/** Зеркало пишет `poly`, своя лента — `polymarket`. Это одна площадка. */
export function isPolymarket(source: string): boolean {
  const code = source.trim().toLowerCase();
  return code === 'poly' || code.startsWith('polymarket');
}

/**
 * Кто первая нога. `auto` — карточка сама (риск и площадка). `poly` — полимаркет
 * всегда первым, как просил Антон.
 */
export type LegsFirst = 'auto' | 'poly';

export function legsFirstOf(value: unknown): LegsFirst {
  return value === 'poly' ? 'poly' : 'auto';
}

type Lead = 'bcgame' | 'thunderpick' | 'thin' | null;

/**
 * Нога, которую берут первой по свойству площадки, а не по риску (28.09, практик):
 * BC.Game показывает лимит только после нажатия, Thunderpick в лайве закрывает
 * ставки, тонкий стакан Polymarket может не налить плановые шары.
 */
function leadOf(leg: RowLeg): Lead {
  const code = leg.source.trim().toLowerCase();
  if (code === 'bcgame') return 'bcgame';
  if (code === 'thunderpick' && leg.eventLine === 'live') return 'thunderpick';
  if (isPolymarket(code) && leg.thin === true) return 'thin';
  return null;
}

const LEAD_RANK: Record<Exclude<Lead, null>, number> = { bcgame: 0, thunderpick: 1, thin: 2 };

function rankOf(leg: RowLeg): number {
  const lead = leadOf(leg);
  if (lead !== null) return LEAD_RANK[lead];
  // Polymarket с глубоким стаканом — второй: маркет-ордер нальётся на принятую
  // конторой сумму, а отказ конторы после купленных шаров стоит банка.
  return isPolymarket(leg.source) ? 4 : 3;
}

/**
 * Ноги в том порядке, в котором их берут. Он же и порядок кнопок.
 *
 * Сначала BC.Game, потом Thunderpick в лайве, потом Polymarket с тонким стаканом.
 * Дальше — живой риск, Polymarket с глубоким стаканом — последним. При равном
 * риске порядок остаётся тем, что пришёл с сервера: переставлять одинаковое было
 * бы хуже, чем не переставлять вовсе — плитки прыгали бы между кадрами.
 *
 * `first === 'poly'` поднимает полимаркет над всеми этими правилами.
 */
export function legOrder(legs: readonly RowLeg[], first: LegsFirst = 'auto'): RowLeg[] {
  return legs
    .map((leg, at) => ({
      leg,
      at,
      risk: riskOf(leg).score,
      rank: rankOf(leg),
      poly: first === 'poly' && isPolymarket(leg.source) ? 0 : 1,
    }))
    .sort((a, b) => a.poly - b.poly || a.rank - b.rank || b.risk - a.risk || a.at - b.at)
    .map((one) => one.leg);
}

const LEAD_WHY: Record<Exclude<Lead, null>, string> = {
  bcgame: 'BC.Game первой: лимит виден только после нажатия',
  thunderpick: 'Thunderpick первой: в лайве закрывает ставки',
  thin: 'Polymarket первым: стакан тонкий',
};

/** Причина, по которой первая из уже расставленных ног стоит первой по площадке. */
export function leadWhy(ordered: readonly RowLeg[], first: LegsFirst = 'auto'): string | null {
  const head = ordered[0];
  if (head === undefined || ordered.length < 2) return null;
  if (first === 'poly' && isPolymarket(head.source)) return 'полимаркет берут первым';
  const lead = leadOf(head);
  if (lead === null) return null;
  if (lead === 'bcgame' && ordered.slice(1).some((leg) => isPolymarket(leg.source))) {
    return `${LEAD_WHY.bcgame}. Polymarket — на принятую сумму`;
  }
  return LEAD_WHY[lead];
}

/**
 * Почему эта нога первая. `null` — порядок ничем не обоснован, и молчать честнее.
 *
 * Причина называется только когда ноги действительно различаются: на паре двух
 * одинаковых контор «первым — он рискованнее» было бы неправдой, а неправда в
 * подписи дороже отсутствующей подписи.
 */
export function firstWhy(legs: readonly RowLeg[], first: LegsFirst = 'auto'): string | null {
  const ordered = legOrder(legs, first);
  const head = ordered[0];
  const second = ordered[1];
  if (head === undefined || second === undefined) return null;
  const lead = leadWhy(ordered, first);
  if (lead !== null) return lead;
  const risk = riskOf(head);
  if (risk.score <= riskOf(second).score) return null;
  return risk.why;
}

/** Слот плитки: контора и сторона. Не индекс — индекс прыгает вместе с сортом. */
export function legSeat(leg: Pick<RowLeg, 'source' | 'side'>): string {
  return `${leg.source}\0${leg.side}`;
}

/**
 * Ноги как на экране в момент клика. Кэфы и суммы едут из `legs`, места — нет.
 *
 * Без замка — живой `legOrder`. Новая нога, которой не было при клике,
 * дописывается в конец, не вставляется между плитками.
 */
export function holdLegs(
  legs: readonly RowLeg[],
  seats?: readonly string[] | null,
  first: LegsFirst = 'auto',
): RowLeg[] {
  const live = legOrder(legs, first);
  if (seats === undefined || seats === null || seats.length === 0) return live;
  const buckets = new Map<string, RowLeg[]>();
  for (const leg of live) {
    const key = legSeat(leg);
    const pile = buckets.get(key);
    if (pile !== undefined) pile.push(leg);
    else buckets.set(key, [leg]);
  }
  const out: RowLeg[] = [];
  const taken = new Set<RowLeg>();
  for (const key of seats) {
    const next = buckets.get(key)?.find((leg) => !taken.has(leg));
    if (next === undefined) continue;
    out.push(next);
    taken.add(next);
  }
  for (const leg of live) {
    if (!taken.has(leg)) out.push(leg);
  }
  return out;
}
